import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import moment from 'moment'
import { FaDownload } from 'react-icons/fa'

import Container from 'components/Container'
import Form from 'components/Form'
import { DateInput } from 'components/Form/date'
import Pagination from 'components/DataTable/Pagination'
import { useToast } from 'hooks/toast'
import { useLoading } from 'hooks/loading'
import api from 'services/api'

import { breadcrumbList } from '../domain/breadcrumb/list'
import { namePageTitle } from '../domain/info'
import { toolsList } from '../domain/tools/list'
import { apiListInstitution, apiReport, apiReportPdf } from '../domain/api'
import { CheckboxMultiSelect } from '../components/CheckboxMultiSelect'
import {
  FilterContainer,
  ReportTable,
  StatusBadge,
  TableHeader
} from './styles'

type ChangeType =
  | 'create_plan'
  | 'delete_plan'
  | 'update_plan'
  | 'cancel'
  | 'new_contract'

type ReportItem = {
  change_type: ChangeType
  change_type_label: string
  institution_name: string
  student_name: string
  father_name: string
  change_date: string
  executed: boolean
  status_label: 'Pendente' | 'Executado'
  contract_id: number
}

type ReportResponse = {
  items: ReportItem[]
  total: number
  page: number
  perPage: number
}

const TYPE_OPTIONS = [
  { value: 'create_plan', label: 'Inclusão de plano' },
  { value: 'delete_plan', label: 'Exclusão de plano' },
  { value: 'update_plan', label: 'Edição' },
  { value: 'cancel', label: 'Cancelamento' },
  { value: 'new_contract', label: 'Novo contrato' }
] as const

const DEFAULT_ITEMS_PER_PAGE = 50

const parseQueryDate = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined
  }
  const parsed = moment(value, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.startOf('day').toDate() : undefined
}

const ContractUpdatesList = (): JSX.Element => {
  const { addToast } = useToast()
  const { activeLoading, disableLoading } = useLoading()
  const history = useHistory()
  const { search } = useLocation()
  const location = new URLSearchParams(search)

  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    parseQueryDate(location.get('start_date'))
  )
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    parseQueryDate(location.get('end_date'))
  )
  const [institutionOptions, setInstitutionOptions] = useState<
    { value: number; label: string }[]
  >([])
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState<
    number[]
  >([])
  const [selectedTypes, setSelectedTypes] = useState<ChangeType[]>([])
  const [items, setItems] = useState<ReportItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE)
  const [, setPageChange] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [issuedAt, setIssuedAt] = useState<string>()

  const reportPayload = useMemo(() => {
    if (!startDate || !endDate) {
      return null
    }
    return {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      institution_ids: selectedInstitutionIds.length
        ? selectedInstitutionIds
        : undefined,
      types: selectedTypes.length ? selectedTypes : undefined
    }
  }, [startDate, endDate, selectedInstitutionIds, selectedTypes])

  const formDefaultValues = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate
    }),
    [startDate, endDate]
  )

  useEffect(() => {
    api
      .get(apiListInstitution())
      .then(res => {
        const ordered = res.data
          .filter((institution: IInstitutionData) => institution.is_active)
          .sort((a: IInstitutionData, b: IInstitutionData) =>
            a.company_social_name.localeCompare(b.company_social_name)
          )
        setInstitutionOptions(
          ordered.map((institution: IInstitutionData) => ({
            value: institution.id,
            label: institution.company_social_name
          }))
        )
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao carregar as instituições',
          description:
            'Houve um erro ao carregar as instituições disponíveis no banco de dados.'
        })
      })
  }, [addToast])

  const syncUrl = useCallback(
    (page: number, perPage: number) => {
      if (!reportPayload) {
        return
      }
      const query: string[] = [
        `start_date=${reportPayload.start_date}`,
        `end_date=${reportPayload.end_date}`,
        `page=${page}`,
        `per_page=${perPage}`
      ]
      if (reportPayload.institution_ids?.length) {
        query.push(`institution_ids=${reportPayload.institution_ids.join(',')}`)
      }
      if (reportPayload.types?.length) {
        query.push(`types=${reportPayload.types.join(',')}`)
      }
      history.replace(`/commercial/contractsUpdates?${query.join('&')}`)
    },
    [history, reportPayload]
  )

  const fetchReport = useCallback(
    async (page: number, perPage: number) => {
      if (!reportPayload) {
        addToast({
          type: 'error',
          title: 'Informe o período',
          description: 'Data inicial e data final são obrigatórias.'
        })
        return
      }
      activeLoading()
      try {
        const { data } = await api.post<ReportResponse>(apiReport(), {
          ...reportPayload,
          page,
          perPage
        })
        setItems(data.items)
        setTotalItems(data.total)
        setCurrentPage(data.page)
        setItemsPerPage(data.perPage)
        setHasSearched(true)
        setIssuedAt(moment().format('DD/MM/YYYY HH:mm:ss'))
        syncUrl(data.page, data.perPage)
      } catch {
        addToast({
          type: 'error',
          title: 'Erro ao carregar relatório',
          description: 'Não foi possível buscar as alterações de contrato.'
        })
      } finally {
        disableLoading()
      }
    },
    [activeLoading, addToast, disableLoading, reportPayload, syncUrl]
  )

  const downloadPdf = async () => {
    if (!reportPayload) {
      return
    }
    activeLoading()
    try {
      const res = await api.post(apiReportPdf(), reportPayload, {
        responseType: 'arraybuffer'
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `Relatorio-alteracoes-contratos-${reportPayload.start_date}-${reportPayload.end_date}.pdf`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao gerar PDF',
        description: 'Não foi possível baixar o relatório.'
      })
    } finally {
      disableLoading()
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(search)
    const start = params.get('start_date')
    const end = params.get('end_date')
    const institutions = params.get('institution_ids')
    const types = params.get('types')
    const pageParam = params.get('page')
    const perPageParam = params.get('per_page')

    if (start) {
      setStartDate(parseQueryDate(start))
    }
    if (end) {
      setEndDate(parseQueryDate(end))
    }
    if (institutions && institutionOptions.length) {
      const ids = institutions.split(',').map(Number)
      setSelectedInstitutionIds(ids)
    }
    if (types) {
      setSelectedTypes(types.split(',') as ChangeType[])
    }
    if (pageParam) {
      setCurrentPage(Number(pageParam) || 1)
    }
    if (perPageParam) {
      setItemsPerPage(Number(perPageParam) || DEFAULT_ITEMS_PER_PAGE)
    }
  }, [search, institutionOptions.length])

  const onSearch = () => {
    setCurrentPage(1)
    fetchReport(1, itemsPerPage)
  }

  const onClear = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setSelectedInstitutionIds([])
    setSelectedTypes([])
    setItems([])
    setTotalItems(0)
    setCurrentPage(1)
    setItemsPerPage(DEFAULT_ITEMS_PER_PAGE)
    setHasSearched(false)
    setIssuedAt(undefined)
    history.replace('/commercial/contractsUpdates')
  }

  const firstItem = totalItems === 0 ? 0 : itemsPerPage * (currentPage - 1) + 1
  const lastItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <Container
      pageTitle={namePageTitle}
      breadcrumb={breadcrumbList}
      tools={toolsList}
    >
      <FilterContainer>
        <Form
          defaultValues={formDefaultValues}
          onSubmit={onSearch}
          className="col-md-12"
        >
          <div className="col-md-12 d-flex flex-wrap">
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data inicial"
                name="start_date"
                onChange={date => setStartDate(date)}
                selected={startDate}
                controlled
              />
            </div>
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data final"
                name="end_date"
                onChange={date => setEndDate(date)}
                selected={endDate}
                controlled
              />
            </div>
            <div className="col-md-4 pe-8">
              <CheckboxMultiSelect
                label="Instituições"
                fieldId="institution_ids"
                options={institutionOptions}
                selectedValues={selectedInstitutionIds}
                onChange={values =>
                  setSelectedInstitutionIds(values.map(Number))
                }
                placeholder="Todas"
              />
            </div>
            <div className="col-md-4">
              <CheckboxMultiSelect
                label="Tipo de alteração"
                fieldId="change_types"
                options={[...TYPE_OPTIONS]}
                selectedValues={selectedTypes}
                onChange={values => setSelectedTypes(values as ChangeType[])}
                placeholder="Todos"
              />
            </div>
          </div>
          <footer className="col-md-12 d-flex justify-content-end pb-5 gap-5">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClear}
            >
              LIMPAR
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              BUSCAR
            </button>
          </footer>
        </Form>
        <hr />
      </FilterContainer>

      {hasSearched && (
        <>
          <TableHeader>
            <div>
              <h3 className="h3 pt-6">Listagem</h3>
              {issuedAt && (
                <p className="issued-at mb-0">Emissão: {issuedAt}</p>
              )}
            </div>
            <button
              type="button"
              className="btn"
              disabled={!totalItems}
              onClick={downloadPdf}
            >
              <FaDownload />
              Baixar relatório
            </button>
          </TableHeader>
          {totalItems === 0 ? (
            <p className="text-muted">Nenhum registro encontrado no período.</p>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-end mt-2 mb-4">
                <label>
                  <select
                    value={itemsPerPage}
                    onChange={e => {
                      const perPage = Number(e.target.value)
                      setItemsPerPage(perPage)
                      setCurrentPage(1)
                      fetchReport(1, perPage)
                    }}
                    className="form-select form-select-solid form-select-sm fw-bold"
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                  <p className="mb-0">resultados por página</p>
                </label>
              </div>
              <ReportTable>
                <thead>
                  <tr>
                    <th className="index-col">#</th>
                    <th>Tipo de alteração</th>
                    <th>Instituição</th>
                    <th>Nome do aluno</th>
                    <th>Nome do pai</th>
                    <th>Data da alteração</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={`${item.contract_id}-${item.change_type}-${
                        firstItem + index
                      }`}
                    >
                      <td className="index-col">{firstItem + index}</td>
                      <td>{item.change_type_label}</td>
                      <td>{item.institution_name}</td>
                      <td>
                        {item.contract_id ? (
                          <Link
                            to={`/commercial/contracts/view/${item.contract_id}`}
                          >
                            {item.student_name}
                          </Link>
                        ) : (
                          item.student_name
                        )}
                      </td>
                      <td>{item.father_name}</td>
                      <td>{item.change_date}</td>
                      <td>
                        <StatusBadge pending={!item.executed}>
                          {item.status_label}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ReportTable>
              <div className="d-flex flex-stack align-items-end h-60px">
                <div className="dataTables_info">
                  Mostrando de {firstItem} até {lastItem} de {totalItems}{' '}
                  {totalItems === 1 ? 'registro' : 'registros'}
                </div>
                <div className="dataTables_paginate paging_bootstrap_number">
                  <Pagination
                    total={totalItems}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={page => {
                      setCurrentPage(page)
                      fetchReport(page, itemsPerPage)
                    }}
                    setIsPageChanged={setPageChange}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default ContractUpdatesList
